// RCFT (Rey Complex Figure Test) — Meyers normes
// Source: Rey Figur_Meyers_normes_tous.pdf
// Parquets: src/db/RCFT-Meyers/
//   rcftmeyersmap.parquet   : age range → block
//   <block>-norms.parquet   : T-score table for Immediate Recall,
//                             Delayed Recall, Recognition Total Correct
//   <block>-rawscore.parquet: %ile bands for Copy and the True/False
//                             Positive/Negative recognition variables
//
// Block selection: child blocks a1..a16 cover 6y0m..17y11m in month-resolved
// ranges; adult blocks c1..c14 cover 18y..89y; "census" is a fallback.
//
// Returns an array of scoresheet entries — one per row the function fills:
//   G71 — Rappel visuo-spatial immédiat (immediate recall PC)
//   G72 — Rappel visuo-spatial différé  (delayed recall PC)
//   G85 — Copie figure complexe         (copy PC)
// Recognition measures are returned in the result object but not yet mapped
// to a scoresheet row.

import { computeEvaluation } from "./scoringEval";
import { formatTimeLine } from "../utils/formatSeconds";
import { makeParquetQuerier } from "./parquetQuery";
import { matchesRange } from "./rangeMatch";

const queryParquet = makeParquetQuerier("RCFT-Meyers");

function parseLowerBound(rangeStr) {
  if (!rangeStr) return null;
  const s = String(rangeStr).trim();
  if (/^>=?/.test(s)) return parseFloat(s.replace(/^>=?\s*/, ""));
  if (/^<=?/.test(s)) return null;
  const range = s.match(/^(-?\d+\.?\d*)\s*[-–]/);
  if (range) return parseFloat(range[1]);
  const num = parseFloat(s);
  return isNaN(num) ? null : num;
}

// Look up a recall raw score in a norms table column → {tScore, pile}.
// Walks T-scores from highest to lowest and returns the first T whose
// recorded raw value is <= rawScore (or the row's range contains rawScore).
function lookupNormsRawToT(normsRows, column, rawScore) {
  for (const row of normsRows) {
    if (matchesRange(rawScore, row[column])) {
      return { tScore: row.t_score, pile: row.pile };
    }
  }
  for (const row of normsRows) {
    const lo = parseLowerBound(row[column]);
    if (lo != null && rawScore >= lo) {
      return { tScore: row.t_score, pile: row.pile };
    }
  }
  return { tScore: null, pile: null };
}

function lookupRawscoreBand(rawscoreRows, column, rawValue) {
  for (const row of rawscoreRows) {
    if (matchesRange(rawValue, row[column])) {
      return row.pile_range;
    }
  }
  return null;
}

// Convert age in years (decimal allowed) to integer months.
function ageYearsToMonths(ageYears, ageMonthsExtra = 0) {
  const years = Math.floor(ageYears);
  return years * 12 + Math.round((ageYears - years) * 12) + ageMonthsExtra;
}

/**
 * Score an RCFT (Meyers normes) test.
 *
 * @param {Object} input
 * @param {number} input.age - patient age in years (decimal allowed, e.g. 7.5)
 * @param {number} [input.ageMonthsExtra] - extra months (alternative to decimal age)
 * @param {string|number} [input.copy] - Copy raw score (0–36)
 * @param {string|number} [input.copyTime] - Copy time in seconds
 * @param {string|number} [input.immediateRecall] - Immediate Recall raw (0–36)
 * @param {string|number} [input.delayedRecall] - Delayed Recall raw (0–36)
 * @param {string|number} [input.recognitionTotal] - Recognition Total Correct (0–24)
 * @param {string|number} [input.truePositives] - Recognition True Positives raw
 * @param {string|number} [input.falsePositives] - Recognition False Positives raw
 * @param {string|number} [input.trueNegatives] - Recognition True Negatives raw
 * @param {string|number} [input.falseNegatives] - Recognition False Negatives raw
 */
export async function calcRcftMeyers(input) {
  const {
    age,
    ageMonthsExtra = 0,
    copy,
    copyTime,
    immediateRecall,
    delayedRecall,
    recognitionTotal,
    truePositives,
    falsePositives,
    trueNegatives,
    falseNegatives,
    lang,
  } = input || {};

  if (age == null || age === "") {
    return { error: lang === "de" ? "Alter fehlt" : "Âge manquant" };
  }
  const ageMonths = ageYearsToMonths(parseFloat(age), ageMonthsExtra);

  const map = await queryParquet("rcftmeyersmap.parquet");

  const specific = map.find(
    (b) =>
      b.block !== "census" &&
      ageMonths >= b.age_min_months &&
      ageMonths <= b.age_max_months
  );
  const block = specific || map.find((b) => b.block === "census");
  if (!block) {
    return {
      error: lang === "de" ? `Kein Block für Alter=${age} gefunden` : `Aucun bloc trouvé pour âge=${age}`,
    };
  }

  const [normsRows, rawscoreRows] = await Promise.all([
    queryParquet(block.norms_file),
    queryParquet(block.rawscore_file),
  ]);

  const num = (v) => (v === "" || v == null ? null : parseFloat(v));

  // Copy raw and copy time are independent percentile-band lookups against
  // the rawscore parquet ("copy" and "time_to_copy" columns).
  const copyN = num(copy);
  const copyPC = copyN != null
    ? lookupRawscoreBand(rawscoreRows, "copy", copyN)
    : null;

  const timeN = num(copyTime);
  const copyTimePC = timeN != null
    ? lookupRawscoreBand(rawscoreRows, "time_to_copy", timeN)
    : null;

  const immN = num(immediateRecall);
  const immNorm = immN != null
    ? lookupNormsRawToT(normsRows, "immediate_recall", immN)
    : { tScore: null, pile: null };

  const delN = num(delayedRecall);
  const delNorm = delN != null
    ? lookupNormsRawToT(normsRows, "delayed_recall", delN)
    : { tScore: null, pile: null };

  const recN = num(recognitionTotal);
  const recNorm = recN != null
    ? lookupNormsRawToT(normsRows, "recognition_total_correct", recN)
    : { tScore: null, pile: null };

  // Recognition variable bands
  const tpPC = num(truePositives) != null
    ? lookupRawscoreBand(rawscoreRows, "true_positives", num(truePositives))
    : null;
  const fpPC = num(falsePositives) != null
    ? lookupRawscoreBand(rawscoreRows, "false_positives", num(falsePositives))
    : null;
  const tnPC = num(trueNegatives) != null
    ? lookupRawscoreBand(rawscoreRows, "true_negatives", num(trueNegatives))
    : null;
  const fnPC = num(falseNegatives) != null
    ? lookupRawscoreBand(rawscoreRows, "false_negatives", num(falseNegatives))
    : null;

  // Build one scoresheet entry per row that the function can fill. Mirrors
  // the `mapCeradToScoresheet` pattern: each entry carries its own resultLoc,
  // resultData, resultText and evaluation.
  const entries = [];

  const pushEntry = (resultLoc, resultData) => {
    const resultText = resultData
      .map((r) => r.rawTemps != null
        ? formatTimeLine(r.label, r.rawTemps, r.value)
        : `${r.label}= ${r.value}${r.tScore ? ` (T=${r.tScore})` : ""}`)
      .join("\n");
    const evaluation = computeEvaluation(resultData, resultLoc);
    entries.push({ resultLoc, resultData, resultText, evaluation });
  };

  const isDe = lang === "de";
  const COPY_LBL = isDe ? "Kopie PC" : "Copie PC";
  const TIME_LBL = isDe ? "Zeit PC" : "Temps PC";

  if (immN != null) {
    pushEntry("G71", [{
      name: "pC",
      label: "PC",
      value: immNorm.pile ?? "—",
      tScore: immNorm.tScore,
      italic: !immNorm.pile,
    }]);
  }

  if (delN != null) {
    pushEntry("G72", [{
      name: "pC",
      label: "PC",
      value: delNorm.pile ?? "—",
      tScore: delNorm.tScore,
      italic: !delNorm.pile,
    }]);
  }

  if (copyN != null || timeN != null) {
    const data = [];
    if (copyN != null) {
      data.push({ name: "copiePC", label: COPY_LBL, value: copyPC ?? "—", italic: !copyPC });
    }
    if (timeN != null) {
      data.push({ name: "tempsPC", label: TIME_LBL, value: copyTimePC ?? "—", italic: !copyTimePC, rawTemps: timeN });
    }
    pushEntry("G85", data);
  }

  return {
    block: block.block,
    entries,
    immediateRecall: { tScore: immNorm.tScore, pile: immNorm.pile },
    delayedRecall: { tScore: delNorm.tScore, pile: delNorm.pile },
    recognitionTotal: { tScore: recNorm.tScore, pile: recNorm.pile },
    copyPC,
    copyTimePC,
    truePositivesPC: tpPC,
    falsePositivesPC: fpPC,
    trueNegativesPC: tnPC,
    falseNegativesPC: fnPC,
  };
}
